import type { TopicImage } from "../types/common";

// 「題目圖片」佔位切段(kanban 114216):
// 素材庫發佈的 chapterDesc 文字裡,【圖片N】只是排版佔位,實際圖檔由同一份
// chapter payload 的 images[]（{n, url, alt}）依 n 對應回來。
// 這裡只做純字串/陣列運算,不碰 DOM、不吃 React —— 三種段落(text/image/placeholder)
// 全部先切好,元件只管「怎麼畫」不管「怎麼切」,邏輯才能用 vitest 直測
// (repo 沒裝 jsdom,元件本身測不了)。
//
// 未在 images[] 找到對應 n 的佔位視為「素材庫漏綁」——防禦性地原樣保留文字
// (raw,含【】),不噴例外、也不讓那段內容整段消失。

export type Segment =
  | { type: "text"; text: string }
  | { type: "image"; img: TopicImage }
  | { type: "placeholder"; raw: string };

const PLACEHOLDER_PATTERN = /【圖片(\d+)】/g;

/**
 * 把 chapterDesc 依【圖片N】佔位切成 text/image/placeholder 段,依原文順序排列。
 * - 佔位前後的文字各自成一段 text(文首/文尾/連續佔位造成的空字串一律過濾,不產生空段)。
 * - 佔位的 N 能在 images 找到對應項 → image 段(帶整個 TopicImage)。
 * - 找不到對應項(素材庫漏綁,或 N 本來就沒有圖)→ placeholder 段,呼叫端原樣印出 raw。
 * - images 陣列裡有、但文字內沒有對應佔位的圖,不會出現在結果裡 —— 圖只在佔位處出現。
 */
export const splitTopicPlaceholders = (
  desc: string,
  images: TopicImage[] | undefined
): Segment[] => {
  const segments: Segment[] = [];
  if (!desc) return segments;

  const imageByN = new Map<number, TopicImage>();
  (images ?? []).forEach((img) => imageByN.set(img.n, img));

  const re = new RegExp(PLACEHOLDER_PATTERN.source, "g");
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(desc)) !== null) {
    const raw = match[0];
    const start = match.index;

    if (start > lastIndex) {
      const text = desc.slice(lastIndex, start);
      if (text !== "") segments.push({ type: "text", text });
    }

    const n = Number(match[1]);
    const img = imageByN.get(n);
    segments.push(img ? { type: "image", img } : { type: "placeholder", raw });

    lastIndex = start + raw.length;
  }

  if (lastIndex < desc.length) {
    const text = desc.slice(lastIndex);
    if (text !== "") segments.push({ type: "text", text });
  }

  return segments;
};
